import React, { useState } from "react";
import { Loader2, Plus, Search, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { akilHafizaCreate, akilHafizaSearch, akilPredictOutcome } from "../../lib/akilApi";
import { AiMarkdown } from "./AiMarkdown";

const HAFIZA_KATEGORILER = [
    { value: "DILEKCE", label: "Dilekçe Şablonu" },
    { value: "TAHKIM_KARARI", label: "Tahkim Hakem Kararı" },
    { value: "MAHKEME_KARARI", label: "Mahkeme Kararı" },
    { value: "MEVZUAT", label: "Hukuki Mevzuat / Yargıtay" },
];

export function AkilHafizaTab() {
    const [query, setQuery] = useState("");
    const [kategori, setKategori] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [form, setForm] = useState({ baslik: "", kategori: "DILEKCE", icerik: "" });
    const [saving, setSaving] = useState(false);

    async function runSearch(e) {
        e?.preventDefault();
        if (!query.trim()) return toast.error("Arama metni girin");
        setLoading(true);
        try {
            const r = await akilHafizaSearch({
                query: query.trim(),
                kategori: kategori || undefined,
                limit: 8,
            });
            setResults(r.results || []);
            if (!(r.results || []).length) toast.info("Sonuç bulunamadı");
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Arama başarısız");
        } finally {
            setLoading(false);
        }
    }

    async function runCreate(e) {
        e.preventDefault();
        if (!form.baslik.trim() || !form.icerik.trim()) {
            return toast.error("Başlık ve içerik zorunlu");
        }
        setSaving(true);
        try {
            await akilHafizaCreate(form);
            toast.success("Kurumsal hafızaya eklendi");
            setForm((s) => ({ ...s, baslik: "", icerik: "" }));
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Kayıt başarısız");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
                <div className="rounded-2xl border border-white/10 p-5 bg-[#0a1018]/60">
                    <div className="flex items-center gap-2 text-[#D4AF37] mb-3">
                        <Search className="h-5 w-5" />
                        <h3 className="font-semibold">Kurumsal Hafıza Arama</h3>
                    </div>
                    <form onSubmit={runSearch} className="space-y-3">
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Dilekçe, karar veya mevzuat ara…"
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm"
                        />
                        <select
                            value={kategori}
                            onChange={(e) => setKategori(e.target.value)}
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm"
                        >
                            <option value="">Tüm kategoriler</option>
                            {HAFIZA_KATEGORILER.map((k) => (
                                <option key={k.value} value={k.value}>
                                    {k.label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#D4AF37] text-black font-semibold py-2.5 text-sm disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            Semantik Ara
                        </button>
                    </form>
                </div>

                <div className="rounded-2xl border border-white/10 p-5 bg-[#0a1018]/60">
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                        <Plus className="h-5 w-5" />
                        <h3 className="font-semibold">Hafızaya Ekle</h3>
                    </div>
                    <form onSubmit={runCreate} className="space-y-3">
                        <input
                            value={form.baslik}
                            onChange={(e) => setForm((s) => ({ ...s, baslik: e.target.value }))}
                            placeholder="Başlık"
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm"
                        />
                        <select
                            value={form.kategori}
                            onChange={(e) => setForm((s) => ({ ...s, kategori: e.target.value }))}
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm"
                        >
                            {HAFIZA_KATEGORILER.map((k) => (
                                <option key={k.value} value={k.value}>
                                    {k.label}
                                </option>
                            ))}
                        </select>
                        <textarea
                            value={form.icerik}
                            onChange={(e) => setForm((s) => ({ ...s, icerik: e.target.value }))}
                            placeholder="İçerik (şablon, karar metni, madde…)"
                            rows={5}
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm"
                        />
                        <button
                            type="submit"
                            disabled={saving}
                            className="w-full rounded-lg border border-emerald-500/40 text-emerald-300 py-2 text-sm hover:bg-emerald-500/10 disabled:opacity-60"
                        >
                            {saving ? "Kaydediliyor…" : "Hafızaya Kaydet"}
                        </button>
                    </form>
                </div>
            </div>

            <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-300">
                    Sonuçlar {results.length ? `(${results.length})` : ""}
                </h4>
                {!results.length ? (
                    <div className="text-sm text-slate-500 py-8 text-center border border-white/5 rounded-xl">
                        Arama yapın veya yeni kayıt ekleyin.
                    </div>
                ) : (
                    results.map((r) => (
                        <div
                            key={r.id}
                            className="rounded-xl border border-white/10 bg-[#121A2F]/50 p-4 space-y-2"
                        >
                            <div className="flex flex-wrap gap-2 items-center">
                                <span className="text-sm font-semibold text-white">{r.baslik}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">
                                    {r.kategori_display || r.kategori}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 line-clamp-4 whitespace-pre-wrap">{r.icerik}</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export function AkilTahminTab() {
    const [dosyaId, setDosyaId] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    async function runPredict(force = false) {
        if (!dosyaId.trim()) return toast.error("Sigorta dosya ID girin");
        setLoading(true);
        setResult(null);
        try {
            const r = await akilPredictOutcome(Number(dosyaId), force ? { force: "1" } : {});
            setResult(r);
            if (r.success === false) toast.error(r.error || "Tahmin üretilemedi");
            else toast.success("Sonuç tahmini hazır");
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Tahmin hatası");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="max-w-3xl space-y-5">
            <div className="rounded-2xl border border-white/10 p-5 bg-[#0a1018]/60">
                <div className="flex items-center gap-2 text-purple-300 mb-2">
                    <TrendingUp className="h-5 w-5" />
                    <h3 className="font-semibold">Dosya Sonuç Tahmini</h3>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Sigorta dosyası verilerine göre AI destekli sonuç / süre / strateji raporu. Mevcut
                    dosya intel API kullanılır; dosya akışına müdahale etmez.
                </p>
                <div className="flex flex-wrap gap-2">
                    <input
                        type="number"
                        min={1}
                        value={dosyaId}
                        onChange={(e) => setDosyaId(e.target.value)}
                        placeholder="Sigorta dosya ID"
                        className="flex-1 min-w-[160px] rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono"
                    />
                    <button
                        type="button"
                        onClick={() => runPredict(false)}
                        disabled={loading}
                        className="px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 disabled:opacity-60"
                    >
                        {loading ? "Hesaplanıyor…" : "Tahmin Üret"}
                    </button>
                    <button
                        type="button"
                        onClick={() => runPredict(true)}
                        disabled={loading}
                        className="px-4 py-2.5 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/5 disabled:opacity-60"
                    >
                        Yeniden (force)
                    </button>
                </div>
            </div>

            {result ? (
                <div className="rounded-2xl border border-white/10 p-5 bg-[#121A2F]/40 space-y-3">
                    {result.metin ? (
                        <AiMarkdown text={result.metin} />
                    ) : (
                        <pre className="text-xs text-slate-300 overflow-auto max-h-[480px] whitespace-pre-wrap font-mono">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    )}
                </div>
            ) : null}
        </div>
    );
}
